import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Flight } from '@ibe/models';
import { BookingStep, getBrandFeatures } from '@ibe/models/ui';
import { ModalService, SidebarService } from '@ibe/services/ui';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import * as fromStore from '../../../../../store';

import { PassengersSegmentedCount } from '@ibe.models/passenger.model.indra.lib';
import { INDRAFeature } from '@ibe.models/ancillary.indra.model.lib';
import { INDRAFareInformation } from '@ibe.models/booked-trip-details.indra.model.lib';
import { INDRAAncillariesService } from '@ibe.services/ancillaries.indra.service';
import { INDRABookingService } from '@ibe.services/booking.indra.service';
import { INDRABFMDataStorageService } from '@ibe.services/bfm-data-storage.indra.service';
import { INDRAIbeAdminConfigService } from '@ibe.services/ibeadmin-config.service';
import { INDRAIbeAdminService } from '@ibe.services/ibeadmin.indra.service';
import { INDRALoginModalComponent } from '@ibe.components/shared/login-modal/login-modal.indra.component';

import { PAX_TYPE_INFANT_CODE,
		ANCILLARY_PRODUCT_GROUP_CODE_MILES,
		SPECIAL_CASE_MARKET_LANGUAGE_MAP,
		SESSION_VAR_KEY_LANGUAGE,
		DEFAULT_LANGUAGE,
		SESSION_VAR_KEY_MARKET,
		DEFAULT_MARKET} from '@ibe.config';

@Component({
	selector: 'app-flight-details-loyalty-miles',
	templateUrl: './details-loyalty-miles.indra.component.html',
	styleUrls: ['./details-loyalty-miles.component.scss']
})
export class INDRAFlightDetailsLoyaltyMilesComponent implements OnInit, OnDestroy {
	flight;
	brand: string;
	feature: INDRAFeature;

	isAlive = true;

	passengers$: Observable<PassengersSegmentedCount> = this.store.select(fromStore.getMiniIBEPassengers);
	passengerList;

	reviewFlights: boolean = false;
	timeToThinkFlights: boolean = false;
	isUserLogin: boolean = false;

	constructor(
		protected sidebarService: SidebarService,
		protected route: ActivatedRoute,
		protected router: Router,
		private store: Store<fromStore.State>,
		private ancillariesService: INDRAAncillariesService,
		private bookingService: INDRABookingService,
		private storage: INDRABFMDataStorageService,
		private ibeAdminConfigService: INDRAIbeAdminConfigService,
		private ibeAdminService: INDRAIbeAdminService,
		private modalService: ModalService,
	) {
	}

	ngOnInit() {		
		if (sessionStorage.getItem("userData")) {
			this.isUserLogin = true;
		} else {
			this.isUserLogin = false;
		}

		if (this.router.url.includes('/review-your-trip')) this.reviewFlights = true;
		if (this.router.url.includes('/time-to-think')) this.timeToThinkFlights = true;
		this.brand = this.route.snapshot.paramMap.get('brand');

		this.passengers$
			.pipe(takeWhile(() => this.isAlive))
				.subscribe(value => {
				if (value) {
					// generate generic passenger list according to type and count
					this.passengerList = this.ancillariesService.INDRA_generateGenericPassengerList(value);
				}
			});

		// Note: There are three flows where this sidebar will be shown - flight search result page, review your trip page, and in time to think page
		if (this.reviewFlights || this.timeToThinkFlights) {
			const flightIdentifier = this.route.snapshot.paramMap.get('flight');
			const flightObj = this.bookingService.INDRA_getBookedFlight(flightIdentifier);
			this.flight = this.bookingService.INDRA_getFlightRoute(flightObj);
			this.feature = this.INDRA_createFeatureObject(flightObj.idFlight.toString(), flightObj.assignedFlightId.toString());
		} else {
			// get flight and feature object from the data obj passed into the sidebarservice
			const parameters = this.sidebarService.getSidebarParameters();
			if (parameters && parameters.length) {
				this.flight = parameters[0].flightInfo;
				this.feature = parameters[0].feature;
				this.INDRA_setDescriptionWithMiles();
			}
		}

		// set ancillary exclusions to the passenger list
		this.INDRA_setPassengerAncillaryExclusions();
	}

	private INDRA_setPassengerAncillaryExclusions() {
		this.passengerList.forEach(passenger => {
			if (passenger.type === PAX_TYPE_INFANT_CODE) {
				passenger.ancillaryStatus = false;
			}
		});
	}
	
	private INDRA_setDescriptionWithMiles() {
		const translatedMilesLabel = this.INDRA_getTranslation('ancillaries.group.miles')
		if (this.feature && this.feature.amountLabel) {
			this.feature.description = this.feature.amountLabel + ' ' + translatedMilesLabel;
		} else {
			this.feature.description = translatedMilesLabel;
		}
	}

	// create feature object only for Review Page flow
	private INDRA_createFeatureObject(flightId, flightCardId) {
		const fareInformation = this.storage.activeBooking['fare-information'];
		const flightIdfareInformation: INDRAFareInformation = fareInformation[flightId];
		const includedAncillaries = [];
		if (flightIdfareInformation) {
			flightIdfareInformation.ancillaries.forEach(ancillaryCode => {
				if (ancillaryCode === ANCILLARY_PRODUCT_GROUP_CODE_MILES) {
					includedAncillaries.push(ancillaryCode);
				}
			});
		}

		const milesTobeEarned = this.bookingService.INDRA_getLoyaltyMilesPerTierByFlightCardId(flightId, null, flightCardId);

		const feature: INDRAFeature = {
			id: ANCILLARY_PRODUCT_GROUP_CODE_MILES,
			code: ANCILLARY_PRODUCT_GROUP_CODE_MILES,
			label: 'Miles',
			labelTranslationKey: 'ancillaries.group.miles',
			icon: null,
			extra: false,
			type: 'added',
			description: milesTobeEarned + ' ' + this.INDRA_getTranslation('ancillaries.group.miles'),
			includedAncillaries: includedAncillaries
		};

		return feature;
	}

	// SIDEBAR INTERACTIONS

	/** Close sidebar and clear secondary route */
	private closeSidebar() {
		this.sidebarService.closeSidebar();
		this.router.navigate([{ outlets: { sidebar: null } }], { replaceUrl: true });
	}

	/** Handles close action intention **/
	INDRA_handleClose() { this.closeSidebar(); }


	ngOnDestroy() {
		this.isAlive = false;
	}

	INDRA_goToUrl(key: string) {
		const languageCode = sessionStorage.getItem(SESSION_VAR_KEY_LANGUAGE) ?
								sessionStorage.getItem(SESSION_VAR_KEY_LANGUAGE).toLowerCase() : DEFAULT_LANGUAGE;
		const languageMap = SPECIAL_CASE_MARKET_LANGUAGE_MAP[languageCode] ? SPECIAL_CASE_MARKET_LANGUAGE_MAP[languageCode] : languageCode;
		const marketCode = sessionStorage.getItem(SESSION_VAR_KEY_LANGUAGE) ?
								sessionStorage.getItem(SESSION_VAR_KEY_MARKET).toLowerCase() : DEFAULT_MARKET;
		const urlIbeAdmin = this.ibeAdminConfigService.getConfigurationValue(key);
		const urlNavigate = urlIbeAdmin.replace('${lang}', `${languageMap}`).replace('${market}', `${marketCode}`);
		return window.open(urlNavigate, '_blank');
	}
	
	INDRA_getTranslation(key: string): string {
		return this.ibeAdminService.getTranslationValue(key);
	}

	INDRA_handleUserClick(userItem, boolean) {
		if (userItem === 'user-info') {			
			this.INDRA_handleClose();
			// Open Login Modal
			this.showLoginModal();
		} 		
	}

	showLoginModal(): any {
		this.modalService.assignComponent(INDRALoginModalComponent);
		this.modalService.open();
	}
}
